class ExpandableItemsElement extends HTMLElement {
  constructor() {
    super();
    this.isExpanded = false;
    this.containerWidth = 0;
    this.resizeObserver = null;
    this.colorsData = [];

    this.toggleExpand = this.toggleExpand.bind(this);
    this.updateContainerWidth = this.updateContainerWidth.bind(this);
    this.handleColorChange = this.handleColorChange.bind(this);
  }

  static get observedAttributes() {
    return ['colors', 'item-expected-width', 'more-button-expected-width', 'gap'];
  }

  connectedCallback() {
    this.itemExpectedWidth = parseInt(this.getAttribute('item-expected-width') || '64', 10);
    this.moreButtonExpectedWidth = parseInt(this.getAttribute('more-button-expected-width') || '64', 10);
    this.gap = parseInt(this.getAttribute('gap') || '8', 10);

    const persistedExpansionState = sessionStorage.getItem('expandableItemsShouldBeExpanded');
    if (persistedExpansionState === 'true') {
      this.isExpanded = true; // Set the state for this new instance
    } else {
      this.isExpanded = false; // Default if no flag
    }

    try {
      const colorsAttr = this.getAttribute('colors');
      if (colorsAttr && colorsAttr.trim() !== "") {
        this.colorsData = JSON.parse(colorsAttr);
      } else {
        this.colorsData = [];
        console.warn('ExpandableItemsElement: "colors" attribute is missing or empty on connect.');
      }
    } catch (e) {
      console.error('ExpandableItemsElement: Failed to parse "colors" attribute JSON on connect.', e);
      console.error('Offending string for "colors" (connect):', this.getAttribute('colors'));
      this.colorsData = [];
    }

    this.render(); // Render based on the determined isExpanded state

    requestAnimationFrame(() => {
      this.updateContainerWidth(); // This will also call render if width changed.
                                  // Render will respect the this.isExpanded state.
      if (!this.resizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateContainerWidth();
        });
        this.resizeObserver.observe(this);
      }
    });
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this);
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    let needsRender = false;
    switch (name) {
      case 'colors':
        try {
          this.colorsData = JSON.parse(newValue || '[]');
        } catch (e) { /* ... error handling ... */ this.colorsData = []; }
        needsRender = true;
        break;
      // ... other cases ...
      case 'item-expected-width':
        this.itemExpectedWidth = parseInt(newValue || '64', 10);
        needsRender = true;
        break;
      case 'more-button-expected-width':
        this.moreButtonExpectedWidth = parseInt(newValue || '64', 10);
        needsRender = true;
        break;
      case 'gap':
        this.gap = parseInt(newValue || '8', 10);
        needsRender = true;
        break;
    }
    if (needsRender && this.isConnected) {
        this.render();
    }
  }

  get _numberOfVisibleProductItems() {
    if (this.isExpanded || this.containerWidth === 0 || this.colorsData.length === 0) {
      return this.colorsData.length;
    }
    const itemFullWidth = this.itemExpectedWidth + this.gap;
    // Calculate max items that can fit WITHOUT the more button
    const maxItemsWithoutMoreButton = Math.floor((this.containerWidth + this.gap) / itemFullWidth);

    if (this.colorsData.length <= maxItemsWithoutMoreButton) {
      return this.colorsData.length;
    } else {
      // Calculate space available when 'more' button IS present
      const availableWidthForProducts = this.containerWidth - this.moreButtonExpectedWidth - this.gap;
      let count = Math.floor((availableWidthForProducts + this.gap) / itemFullWidth);
      return Math.max(0, count);
    }
  }

  get _displayedProductItems() {
    if (this.isExpanded) {
      return this.colorsData;
    }
    return this.colorsData.slice(0, this._numberOfVisibleProductItems);
  }

  get _shouldShowMoreButton() {
    if (this.isExpanded || this.containerWidth === 0 || this.colorsData.length === 0) {
      return false;
    }
    const itemFullWidth = this.itemExpectedWidth + this.gap;
    const maxItemsWithoutMoreButton = Math.floor((this.containerWidth + this.gap) / itemFullWidth);
    return this.colorsData.length > maxItemsWithoutMoreButton;
  }

  get _itemsInMoreButtonCount() {
    if (!this._shouldShowMoreButton) {
      return 0;
    }
    return this.colorsData.length - this._displayedProductItems.length;
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      sessionStorage.setItem('expandableItemsShouldBeExpanded', 'true');
    } else {
      sessionStorage.removeItem('expandableItemsShouldBeExpanded');
    }
    this.render();
  }

  handleColorChange(event) {
    const clickedElement = event.currentTarget;
    if (window.productFormVue && typeof window.productFormVue.changeColor === 'function') {
      window.productFormVue.changeColor(event, clickedElement);
    } else { /* ... error handling ... */ }
  }

  updateContainerWidth() {
    const currentOffsetWidth = this.offsetWidth;
    if ((currentOffsetWidth !== this.containerWidth && currentOffsetWidth > 0) || (this.containerWidth === 0 && currentOffsetWidth > 0)) {
      this.containerWidth = currentOffsetWidth;
      this.render(); // Re-render if width changes
    } else if (currentOffsetWidth === 0 && this.containerWidth !== 0) {
      this.containerWidth = 0; // Element became hidden
    }
  }

  render() {
    this.innerHTML = ''; 
    const wrapper = document.createElement('div');
    wrapper.className = 'product-color-swatch flex flex-wrap ai-center';
    wrapper.style.columnGap = `${this.gap}px`;
    wrapper.style.rowGap = `${this.gap}px`;

    if (this.isExpanded) {
      wrapper.classList.add('expanded-layout');
    }

    this._displayedProductItems.forEach(color => {
      const anchor = document.createElement('a');
      anchor.href = color.url;
      anchor.title = color.color_name;
      anchor.classList.add('each');
      if (color.is_active) {
        anchor.classList.add('active');
      }
      anchor.setAttribute('data-color-name', color.color_name);
      anchor.setAttribute('data-product-handle', color.product_handle);
      anchor.setAttribute('data-product', color.product_json);
      anchor.addEventListener('click', this.handleColorChange);

      const colorElDiv = document.createElement('div');
      colorElDiv.className = 'color-el';

      if (color.color_image_html) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = color.color_image_html;
        while(tempDiv.firstChild) {
            colorElDiv.appendChild(tempDiv.firstChild);
        }
      }
      anchor.appendChild(colorElDiv);
      wrapper.appendChild(anchor);
    });

    if (this._shouldShowMoreButton) { // This getter now correctly reflects the sticky expanded state
      const moreButton = document.createElement('div');
      moreButton.className = 'p2 each more-button flex jc-center ai-center';
      moreButton.textContent = `+${this._itemsInMoreButtonCount}`;
      moreButton.addEventListener('click', this.toggleExpand);
      wrapper.appendChild(moreButton);
    }
    this.appendChild(wrapper);
  }
}

// Define the custom element
if (!customElements.get('expandable-items-element')) {
  customElements.define('expandable-items-element', ExpandableItemsElement);
}